import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import {
  Children,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type ComponentPropsWithoutRef,
  type FC,
} from 'react';
import { cn } from '../lib/utils';
import { usePortalContainer } from './PortalContainer';

import {
  DialogClose as ShadcnDialogClose,
  DialogDescription as ShadcnDialogDescription,
  DialogOverlay as ShadcnDialogOverlay,
  DialogPortal as ShadcnDialogPortal,
  DialogTitle as ShadcnDialogTitle,
} from './ui/dialog';

export type DialogTitleProps = ComponentPropsWithoutRef<typeof ShadcnDialogTitle>;
export const DialogTitle: FC<DialogTitleProps> = (props) => {
  // Unconditional (not `dark:`-prefixed) so it tracks `--foreground-primary` via plain
  // CSS custom property inheritance, the same way DialogContent's own `bg-background`
  // does — matches DialogDescription below, which already gets this right.
  return (
    <ShadcnDialogTitle
      {...props}
      className={cn('text-foreground-primary heading-lg', props.className)}
    />
  );
};

export type DialogDescriptionProps = ComponentPropsWithoutRef<typeof ShadcnDialogDescription>;
export const DialogDescription: FC<DialogDescriptionProps> = (props) => {
  return (
    // `body-sm` (12px, leading-snug) — matches Figma's "Dialog" (6996:1133) description
    // exactly; the previous `body-md` (14px) didn't.
    <ShadcnDialogDescription
      {...props}
      className={cn('text-foreground-secondary body-sm', props.className)}
    />
  );
};

// Figma "Dialog" (design system 6996:1133) width + light/dark surface.
// Default width is `s` (480px); default surface is light.
//
// DialogHeader/DialogFooter below bleed to the edges with `-mx-6 -mt-6` / `-mx-6 -mb-6`,
// which assumes this DialogContent keeps `ui/dialog.tsx`'s default `p-6`. Don't pair
// DialogHeader/DialogFooter with a `className` that shrinks that padding (e.g. `p-0`) — the
// bleed will pull the separator/border outside the box instead. A dialog that needs a
// custom-padding layout should skip DialogHeader/DialogFooter and roll its own chrome, the
// way `CopyToAgentDialog` does.
// Position, surface, and enter/exit motion, copied from the generated `ui/dialog.tsx`
// DialogContent. This wrapper renders the Radix Content itself so it can own the
// portal and the overlay. Keep it easy to diff against `ui/dialog.tsx` if that
// file regenerates.
const radixContentBaseClasses =
  'fixed left-[50%] top-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:max-w-lg';

const dialogContentVariants = cva(
  [
    radixContentBaseClasses,
    // Unconditional (not `dark:`-prefixed) so any plain/unstyled content dropped
    // directly in DialogContent — not just DialogTitle/DialogDescription, which
    // already pin their own tokens — tracks `--foreground-primary` via ordinary
    // CSS custom property inheritance, the same way `bg-background` below does.
    // That inheritance reaches the dialog regardless of *how* something upstream
    // put `.dark` on an ancestor, so this doesn't need its own dark: variant.
    'tw rounded-lg border-border-muted text-foreground-primary shadow-[0px_10px_10px_-5px_rgba(0,64,76,0.05),0px_2px_25px_-5px_rgba(0,64,76,0.1)]',
    'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-1/2 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-1/2',
  ],
  {
    variants: {
      size: {
        xs: 'sm:max-w-[360px]',
        s: 'sm:max-w-[480px]',
        m: 'sm:max-w-[720px]',
        l: 'sm:max-w-[960px]',
      },
      variant: {
        light: '',
        // Everything theme-reactive (background, text) is already covered above via
        // token inheritance. What's left here is a deliberate design choice that
        // can't be auto-detected: which surface tier this dialog sits on
        // (`background-surface`, not the default `background`), plus forcing native
        // form-control/scrollbar rendering (`[color-scheme:dark]`) for a dialog that's
        // dark regardless of the surrounding app's theme. `.dark` is applied directly
        // here (not `dark:`-prefixed) because `@custom-variant dark` is
        // `&:is(.dark *)` and doesn't match the `.dark` node itself.
        dark: 'dark bg-background-surface [color-scheme:dark]',
      },
    },
    defaultVariants: {
      size: 's',
      variant: 'light',
    },
  },
);

export type DialogSize = NonNullable<VariantProps<typeof dialogContentVariants>['size']>;
export type DialogVariant = NonNullable<VariantProps<typeof dialogContentVariants>['variant']>;

const DialogCloseButton: FC<{ className?: string; 'data-testid'?: string }> = ({
  className,
  'data-testid': dataTestId,
}) => (
  <ShadcnDialogClose
    data-testid={dataTestId}
    data-slot="dialog-close"
    className={cn(
      'flex size-9 shrink-0 items-center justify-center rounded-lg p-1 text-foreground-secondary transition-colors hover:bg-background-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
      className,
    )}
  >
    <X className="size-4" />
    <span className="sr-only">Close</span>
  </ShadcnDialogClose>
);

// Lets a DialogHeader — however deeply it's nested (inside a <form>, a step
// component picked by parent state, react-hook-form's <Form>, etc.) — claim
// responsibility for the close button, so DialogContent knows to skip its own
// corner fallback instead of rendering a duplicate. A prop/children check from
// DialogContent can't see a header nested inside another component's own render
// output (e.g. LayoutSelectorDialog swaps in a whole <TemplatePickerStep/>), so
// this has to be a runtime registration, not a static one.
type DialogChromeContextValue = {
  showCloseButton: boolean;
  setHeaderHandlesClose: (present: boolean) => void;
};
const DialogChromeContext = createContext<DialogChromeContextValue | null>(null);

export type DialogHeaderProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Renders `DialogContent`'s close button as a real flex sibling of this header's
   * title (its first child — the title itself, or a consumer's own custom title
   * row; see `FormulaEditorDialog`), matching Figma's "Header Content"
   * (5686:36540). Every example in the design system only ever has a title plus a
   * single short description line, so Figma's own "center against the whole
   * header" layout happens to look right there — but a longer description that
   * wraps to multiple lines pulls that block's vertical center down with it,
   * visibly drifting the button away from the title. Pairing the button with just
   * the title row instead keeps their vertical centers locked together no matter
   * how much description text follows underneath (including none at all). This
   * header notifies `DialogContent` (via context) so it can skip its own corner
   * button — never a duplicate, however deeply this header is nested. Set to
   * `false` when the dialog has no close button at all (`DialogContent`'s
   * `showCloseButton={false}`), e.g. `ConfirmationDialog`.
   * @default true
   */
  hasCloseButton?: boolean;
  /** Test id for the header close button. */
  closeTestId?: string;
};
export const DialogHeader: FC<DialogHeaderProps> = ({
  className,
  hasCloseButton = true,
  closeTestId,
  children,
  ...props
}) => {
  const chrome = useContext(DialogChromeContext);
  // Only actually show the button if the enclosing DialogContent wants one at all
  // (`showCloseButton`) — `hasCloseButton` alone just means "I'm willing to host it".
  const showsCloseButton = hasCloseButton && (chrome?.showCloseButton ?? true);

  // useLayoutEffect (not useEffect) so the registration + DialogContent's resulting
  // re-render both happen before paint — otherwise the corner fallback button would
  // flash on screen for a frame before this header's own button takes over.
  useLayoutEffect(() => {
    chrome?.setHeaderHandlesClose(showsCloseButton);
    return () => chrome?.setHeaderHandlesClose(false);
  }, [chrome, showsCloseButton]);

  const [titleRow, ...rest] = Children.toArray(children);

  return (
    <div
      data-slot="dialog-header"
      className={cn(
        // Figma's "Header Content" (5686:36540) is a self-contained px-24/py-16
        // box — the same 16px it gives the space below the separator, not
        // DialogContent's own 24px. -mx-6 -mt-6 escapes DialogContent's p-6 on
        // those two edges so `px-6 py-4` here can set the real Figma padding.
        // `mb-2` stacks with DialogContent's `gap-4` (16px, from ui/dialog.tsx) to
        // make the separator-to-content gap 24px, matching Figma's ModalContent
        // `p-24`.
        '-mx-6 -mt-6 mb-2 flex flex-col gap-2 border-b border-border-muted px-6 py-4 text-center sm:text-left',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">{titleRow}</div>
        {showsCloseButton && <DialogCloseButton data-testid={closeTestId} />}
      </div>
      {rest}
    </div>
  );
};

export type DialogFooterProps = ComponentPropsWithoutRef<'div'>;
// Footer content/buttons are implementation-specific and vary per dialog — no fixed
// slot beyond the row itself. A left-anchored element (e.g. a help link) needs no
// dedicated prop either: put `mr-auto` on the first child and `justify-end` pushes
// everything after it to the right, same as it would with any flex row.
export const DialogFooter: FC<DialogFooterProps> = ({ className, ...props }) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      // Mirrors DialogHeader: escape DialogContent's bottom/horizontal p-6 so `px-6
      // py-4` can match Figma's "Footer Content" (5686:36574) box exactly (24px edges
      // become 16px around the buttons), and `mt-2` + the inherited `gap-4` make the
      // content-to-separator gap 24px, same as DialogHeader's `mb-2`.
      '-mx-6 -mb-6 mt-2 flex flex-col-reverse gap-2 border-t border-border-muted px-6 py-4 sm:flex-row sm:justify-end',
      className,
    )}
    {...props}
  />
);

export type DialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  overlayClassName?: string;
  /** Prevents Escape from closing the dialog (in-progress edits, nested shortcuts, etc.). */
  disableEscapeKeyDown?: boolean;
  /** Figma's four Dialog widths (360/480/720/960px). Defaults to `s` (480px). */
  size?: DialogSize;
  /** Light (default) or dark surface. Dark is for portaled dialogs on a dark page. */
  variant?: DialogVariant;
  /**
   * Where the dialog portals to. Defaults to the `PortalContainerProvider`
   * target, and then to `document.body`.
   */
  container?: ComponentPropsWithoutRef<typeof ShadcnDialogPortal>['container'];
};
export const DialogContent: FC<DialogContentProps> = ({
  showCloseButton = true,
  overlayClassName,
  disableEscapeKeyDown,
  onEscapeKeyDown,
  size = 's',
  variant = 'light',
  container,
  children,
  ...props
}) => {
  // A DialogHeader — wherever it ends up in the tree, including nested inside a
  // <form> or swapped in by a parent's own step/state logic (see
  // LayoutSelectorDialog) — registers itself via context when it renders the close
  // button inline, so it can center on its own actual height instead of guessing a
  // fixed offset. Only fall back to the corner overlay (Figma has no spec for this
  // — no dialog in the design system skips the header) when nothing has claimed it.
  const [headerHandlesClose, setHeaderHandlesClose] = useState(false);
  const registerHeaderClose = useCallback((present: boolean) => setHeaderHandlesClose(present), []);
  const providedContainer = usePortalContainer();

  // Because the dialog mounts outside the main app div, we need to
  // add the "tw" class here to get Tailwind styles.
  return (
    <ShadcnDialogPortal container={container ?? providedContainer}>
      <ShadcnDialogOverlay className={cn('tw', variant === 'dark' && 'dark', overlayClassName)} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        {...props}
        onEscapeKeyDown={(e) => {
          if (disableEscapeKeyDown) e.preventDefault();
          onEscapeKeyDown?.(e);
        }}
        className={cn(dialogContentVariants({ size, variant }), props.className)}
      >
        <DialogChromeContext.Provider
          value={{ showCloseButton, setHeaderHandlesClose: registerHeaderClose }}
        >
          {children}
        </DialogChromeContext.Provider>
        {showCloseButton && !headerHandlesClose && (
          <DialogCloseButton className="absolute right-6 top-6" />
        )}
      </DialogPrimitive.Content>
    </ShadcnDialogPortal>
  );
};

export { Dialog, DialogClose, DialogOverlay, DialogPortal, DialogTrigger } from './ui/dialog';
